<template>
  <div class="mb-4">
    <DrawerRowTitle
      :title="
        $t(
          'knowledge.drawers.content-view-setting.settings.access.title-public',
        )
      "
      :tooltip="
        $t(
          'knowledge.drawers.content-view-setting.settings.access.tooltip-public',
        )
      "
    />
    <div v-loading="loading" v-if="companyHostname">
      <!-- URL COPY -->
      <div class="settings__public-item">
        <div class="label">
          {{ $t('knowledge.drawers.content-view-setting.settings.public.url') }}
        </div>
        <el-input :value="url" readonly @focus="$event.target.select()">
          <template slot="append">
            <div
              class="copy-icon"
              v-clipboard:copy="url"
              @click="copyUrl('url')"
              :class="{ 'click-bounce': animate.url }"
            >
              {{
                $t(
                  'knowledge.drawers.content-view-setting.settings.public.copy',
                )
              }}
            </div>
          </template>
        </el-input>
      </div>

      <!-- META -->
      <div class="settings__public-item">
        <div class="label">
          {{
            $t('knowledge.drawers.content-view-setting.settings.public.meta')
          }}
          <el-tooltip
            :content="
              $t(
                'knowledge.drawers.content-view-setting.settings.public.meta-tooltip',
              )
            "
            placement="top"
          >
            <font-awesome-icon
              :icon="['fad', 'info-circle']"
              class="info-icon mx-2"
            />
          </el-tooltip>
          <div class="toggle">
            <button
              class="reveal"
              @click="displayDescription = !displayDescription"
            >
              <div :class="{ 'open-reveal': displayDescription }">
                <font-awesome-icon :icon="['fal', 'chevron-right']" />
              </div>
            </button>
          </div>
        </div>

        <not-allowed-tooltip
          :user-permissions="userPermissions"
          permission-key="canUpdateContent"
        >
          <template v-slot:main-content="{ hasPermissions }">
            <el-input
              v-if="displayDescription"
              type="textarea"
              :disabled="!hasPermissions"
              :rows="3"
              :placeholder="
                $t(
                  'knowledge.drawers.content-view-setting.settings.public.meta-placeholder',
                )
              "
              v-model="newDescription"
            />
          </template>
        </not-allowed-tooltip>
        <div v-if="modified" class="settings__public-cta">
          <el-button @click="handleCancel">{{
            $t('knowledge.drawers.content-view-setting.settings.public.cancel')
          }}</el-button>
          <el-button @click="handleUpdate" type="primary">{{
            $t('knowledge.drawers.content-view-setting.settings.public.update')
          }}</el-button>
        </div>
      </div>

      <!-- COVER -->
      <div class="settings__public-item">
        <div class="label mb-1">
          {{
            $t('knowledge.drawers.content-view-setting.settings.public.cover')
          }}
          <el-tooltip
            :content="
              $t(
                'knowledge.drawers.content-view-setting.settings.public.cover-tooltip',
              )
            "
            placement="top"
          >
            <font-awesome-icon
              :icon="['fad', 'info-circle']"
              class="info-icon mx-2"
            />
          </el-tooltip>
        </div>
        <!-- IMAGE PLACEHOLDER -->

        <not-allowed-tooltip
          :user-permissions="userPermissions"
          permission-key="canUpdateContent"
        >
          <template v-slot:main-content="{ hasPermissions }">
            <div v-if="!cover" class="w-100">
              <div
                class="img-placeholder-wrapper-empty"
                @click="hasPermissions ? handleModifyCover() : ''"
                @mouseover="hover = true"
                @mouseleave="hover = false"
              >
                <img
                  v-if="hover"
                  :src="require('assets/upload-placeholder.gif')"
                  class="img-placeholder-hover"
                />
                <img
                  v-else
                  :src="require('assets/upload-placeholder.svg')"
                  class="img-placeholder"
                />
              </div>
            </div>
            <div v-else>
              <div class="img-placeholder-wrapper-preview">
                <img :src="cover" class="img-placeholder-hover" />
              </div>
              <div class="settings__public-cta">
                <el-button
                  @click="handleDeleteCover"
                  :disabled="!hasPermissions"
                  >{{
                    $t(
                      'knowledge.drawers.content-view-setting.settings.public.cover-delete',
                    )
                  }}</el-button
                >
                <el-button
                  @click="handleModifyCover"
                  type="primary"
                  :disabled="!hasPermissions"
                  >{{
                    $t(
                      'knowledge.drawers.content-view-setting.settings.public.cover-modify',
                    )
                  }}</el-button
                >
              </div>
            </div>
          </template>
        </not-allowed-tooltip>
      </div>
      <!-- IFRAME -->
      <div class="settings__public-item">
        <div class="label">
          {{
            $t('knowledge.drawers.content-view-setting.settings.public.iframe')
          }}
          <el-tooltip
            :content="
              $t(
                'knowledge.drawers.content-view-setting.settings.public.iframe-tooltip',
              )
            "
            placement="top"
          >
            <font-awesome-icon
              :icon="['fad', 'info-circle']"
              class="info-icon mx-2"
            />
          </el-tooltip>
          <div class="toggle">
            <button class="reveal" @click="displayIframe = !displayIframe">
              <div :class="{ 'open-reveal': displayIframe }">
                <font-awesome-icon :icon="['fal', 'chevron-right']" />
              </div>
            </button>
          </div>
        </div>

        <div v-if="displayIframe">
          <el-input :value="iframeUrl" readonly @focus="$event.target.select()">
            <template slot="append">
              <div
                class="copy-icon"
                v-clipboard:copy="iframeUrl"
                @click="copyUrl('iframeUrl')"
                :class="{ 'click-bounce': animate.iframeUrl }"
              >
                {{
                  $t(
                    'knowledge.drawers.content-view-setting.settings.public.copy',
                  )
                }}
              </div>
            </template>
          </el-input>
          <div class="label">
            {{
              $t(
                'knowledge.drawers.content-view-setting.settings.public.iframe-code',
              )
            }}
          </div>

          <el-input
            :value="iframeCode"
            readonly
            @focus="$event.target.select()"
          >
            <template slot="append">
              <div
                class="copy-icon"
                v-clipboard:copy="iframeCode"
                @click="copyUrl('iframeCode')"
                :class="{ 'click-bounce': animate.iframeCode }"
              >
                {{
                  $t(
                    'knowledge.drawers.content-view-setting.settings.public.copy',
                  )
                }}
              </div>
            </template>
          </el-input>
        </div>
      </div>

      <!-- RELATED CONTENT -->
      <ContentViewSettingsPublicRelated
        v-if="focusKnowledgeIsPublic && hasRelatedPlugins"
        :related-contents="relatedContents"
        :user-permissions="userPermissions"
        @update-related-contents="$emit('update-related-contents', $event)"
      />

      <!-- PINNED CONTENT -->
      <div
        class="settings__public-item"
        v-if="focusKnowledgeIsPublic && hasPinnedPlugins"
      >
        <div class="label">
          {{
            $t(
              'knowledge.drawers.content-view-setting.settings.public.pinned-content',
            )
          }}
          <el-tooltip
            :content="
              $t(
                'knowledge.drawers.content-view-setting.settings.public.pinned-content-tooltip',
              )
            "
            placement="top"
          >
            <font-awesome-icon
              :icon="['fad', 'info-circle']"
              class="info-icon mx-2"
            />
          </el-tooltip>
          <div class="toggle">
            <not-allowed-tooltip
              :user-permissions="userPermissions"
              permission-key="canUpdateContent"
              placement="top"
            >
              <template v-slot:main-content="{ hasPermissions }">
                <el-switch
                  v-model="switchPinnedContent"
                  @input="handlePinnedContent"
                  :disabled="!hasPermissions"
                ></el-switch>
              </template>
            </not-allowed-tooltip>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DrawerRowTitle from '@/components/Drawers/Commons/DrawerRowTitle';
import ContentViewSettingsPublicRelated from '@/components/Drawers/ContentViewSettingDrawer/ContentViewSettingsComponents/ContentViewSettingsPublic/ContentViewSettingsPublicRelated.vue';
import NotAllowedTooltip from '@/components/Commons/NotAllowedTooltip';

export default {
  name: 'content-view-settings-public',
  components: {
    DrawerRowTitle,
    ContentViewSettingsPublicRelated,
    NotAllowedTooltip,
  },
  props: {
    publicData: {
      type: Object,
      required: false,
    },
    cover: {
      type: String,
      required: false,
    },
    relatedContents: {
      type: Array,
      required: false,
    },
    userPermissions: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      newDescription: '',
      animate: { url: false, iframeUrl: false, iframeCode: false },
      displayDescription: false,
      displayIframe: false,
      hover: false,
      switchPinnedContent: false,
      pinnedContents: [],
    };
  },
  computed: {
    hasPinnedPlugins() {
      return (
        this.focusKnowledge.plugins.findIndex(
          (p) => p.type === 'PINNED_CONTENTS',
        ) > -1
      );
    },
    hasRelatedPlugins() {
      return (
        this.focusKnowledge.plugins.findIndex(
          (p) => p.type === 'RELATED_CONTENTS',
        ) > -1
      );
    },
    loading() {
      return !this.publicData;
    },
    modified() {
      return this.newDescription !== this.description;
    },
    lang() {
      return this.focusKnowledgeIsMultilinguale
        ? `${this.editingLanguage}/`
        : '';
    },
    url() {
      if (!this.publicData) return '';

      const { slug, id } = this.publicData;
      return `https://${this.companyHostname}/${this.lang}contents/${slug}-${id}`;
    },
    iframeUrl() {
      if (!this.publicData) return '';

      const { slug, id } = this.publicData;
      return `https://${this.companyHostname}/${this.lang}embed/${slug}-${id}`;
    },
    iframeCode() {
      if (!this.publicData) return '';

      const { slug, id } = this.publicData;
      return `<iframe src="https://${this.companyHostname}/${this.lang}embed/${slug}-${id}"></iframe>`;
    },
    description() {
      if (!this.publicData) return '';

      const { description } = this.publicData;
      return description;
    },
    relatedContentIds() {
      return this.relatedList.map((content) => content.id);
    },
    ...mapGetters('adminModule', ['companyHostname']),
    ...mapGetters('knowledgeModule', [
      'focusKnowledge',
      'focusKnowledgeId',
      'focusKnowledgeIsPublic',
      'focusContent',
      'editingLanguage',
      'focusKnowledgeIsMultilinguale',
    ]),
  },
  watch: {
    publicData() {
      this.newDescription = this.publicData ? this.publicData.description : '';
    },
    relatedList() {
      this.$emit('update-related-contents', this.relatedContentIds);
    },
  },
  async mounted() {
    const { pinnedContents } = await this.getPinnedContents(
      this.focusKnowledgeId,
    );

    this.pinnedContents = pinnedContents;
    this.contentId = this.$route.params.contentId;
    this.switchPinnedContent = this.pinnedContents.includes(this.contentId);

    this.newDescription = this.publicData ? this.publicData.description : '';
  },
  methods: {
    copyUrl(type) {
      this.animate[type] = true;
      this.$message({
        message: this.$t(
          'knowledge.actions.copy-to-clipboard-confirmation-message',
        ),
        type: 'info',
      });
      setTimeout(() => {
        this.animate[type] = false;
      }, 500);
    },
    handleUpdate() {
      this.$emit('update-public-description', this.newDescription);
    },
    handleCancel() {
      this.newDescription = this.description;
    },
    handleModifyCover() {
      this.$emit('open-cover-modal');
    },
    handleDeleteCover() {
      this.$emit('delete-cover');
    },
    handlePinnedContent() {
      const pos = this.pinnedContents.indexOf(this.contentId);
      if (pos > -1) this.pinnedContents.splice(pos, 1);
      else this.pinnedContents.push(this.contentId);

      this.updatePinnedContents({
        knowledgeId: this.focusKnowledgeId,
        pinnedContents: this.pinnedContents,
      });
    },
    ...mapActions('knowledgeModule', [
      'getPinnedContents',
      'updatePinnedContents',
    ]),
  },
};
</script>

<style lang="scss" scoped>
:deep() .el-input__inner {
  padding: 4px 8px;
  height: 28px;
  background-color: #f5f7fa;
  border-color: #e4e7ed;
  color: $grey-7-mayday;
}

:deep() .el-textarea__inner {
  padding: 4px 8px;
}

:deep() .el-input-group__append {
  padding: 0;
  width: 28px;
  height: 28px;
  background-color: white;
}

.copy-icon {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: $grey-7-mayday;
  width: 100%;
  padding: 0 8px;
  font-size: 12px;
  * {
    width: 14px;
    height: 18px;
  }
  transition: transform 200ms ease;

  &:hover {
    transform: scale(1.1);
    color: $blue-mayday;
  }
}

.click-bounce {
  animation: bounce 500ms ease;
}

@keyframes bounce {
  0%,
  20%,
  50%,
  80%,
  100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-10px);
  }
  60% {
    transform: translateY(-5px);
  }
}

.settings__public-item {
  margin-bottom: 12px;
}

.label {
  font-size: 14px;
  font-weight: 700;
  position: relative;
}

.toggle {
  position: absolute;
  right: 0;
  top: 0;
}

:deep() .el-switch__core {
  height: 16px;
  width: 28px !important;
}

:deep() .el-switch__core:after {
  width: 12px;
  height: 12px;
}

:deep() .el-switch.is-checked .el-switch__core::after {
  margin-left: -13px;
}

.info-icon {
  color: $grey-5-mayday;
  font-size: 12px;
}

.settings__public-cta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;
}

:deep() .el-button {
  padding: 4px 8px;
  font-size: 12px;
}

.cover-wrapper {
  margin-top: 8px;
  display: flex;
  justify-content: center;
  .cover-img {
    height: 100px;
    object-fit: contain;
  }
}

.img-placeholder-wrapper {
  height: 100px;
  display: flex;
  align-items: center;
  border: 1px $grey-4-mayday solid;
}
.img-placeholder-wrapper-empty {
  height: 100px;
  background-color: $grey-1-mayday;
  border: 1px $grey-4-mayday dashed;
  justify-content: center;
  &:hover {
    border: 1px $blue-mayday dashed;
    background-color: $grey-1-mayday;
    filter: drop-shadow(0px 15px 15px rgba(112, 144, 176, 0.2));
  }
}

.img-placeholder {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 12px 0;
  margin-left: -6px;
}

.img-placeholder-hover {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.img-placeholder-wrapper-preview {
  max-width: 100%;
  display: flex;
  align-items: center;
}

.reveal {
  width: 18px;
  height: 18px;
  transform-origin: center;

  font-size: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  outline: none;
  background-color: $grey-1-mayday;
  &:hover {
    color: $blue-mayday;
    background: rgba($blue-mayday, 0.1);
  }

  * {
    font-size: 10px;
    display: flex;
    justify-content: center;
    align-items: center;
    transition: transform 200ms ease;
  }
  .open-reveal {
    transform: rotate(90deg);
  }
}
</style>
